/**
 * @file Frontend API client — fuelDensitiesAdminService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";

/** Type definition — Fuel Density Admin. */
export interface FuelDensityAdmin {
  id: number;
  fuelName: string;
  densityKgM3: string;
  densityKgL: string;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Type definition — Fuel Density Create Input. */
export interface FuelDensityCreateInput {
  fuelName: string;
  densityKgM3: number | string;
  densityKgL: number | string;
  sortOrder?: number;
  isActive?: boolean;
}

/** Type definition — Fuel Density Update Input. */
export type FuelDensityUpdateInput = Partial<FuelDensityCreateInput>;

function apiMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Request failed"
  );
}

/** Exported const — fuel Densities Admin Service. */
export const fuelDensitiesAdminService = {
  async list(): Promise<FuelDensityAdmin[]> {
    try {
      const res = await axiosClient.get<{
        success?: boolean;
        data?: FuelDensityAdmin[];
      }>(API_URLS.ADMIN.FUEL_DENSITIES.LIST);
      if (res.data?.success === false) {
        throw new Error(
          (res.data as { message?: string }).message ||
            "Failed to list fuel densities"
        );
      }
      const rows = res.data?.data;
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async getOne(id: number): Promise<FuelDensityAdmin> {
    try {
      const res = await axiosClient.get<{
        success?: boolean;
        data?: FuelDensityAdmin;
      }>(API_URLS.ADMIN.FUEL_DENSITIES.BY_ID(id));
      if (res.data?.success === false || !res.data?.data) {
        throw new Error(
          (res.data as { message?: string })?.message || "Not found"
        );
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async create(body: FuelDensityCreateInput): Promise<FuelDensityAdmin> {
    try {
      const res = await axiosClient.post<{
        success?: boolean;
        data?: FuelDensityAdmin;
      }>(API_URLS.ADMIN.FUEL_DENSITIES.LIST, body);
      if (res.data?.success === false || !res.data?.data) {
        throw new Error(
          (res.data as { message?: string })?.message || "Create failed"
        );
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async update(
    id: number,
    body: FuelDensityUpdateInput
  ): Promise<FuelDensityAdmin> {
    try {
      const res = await axiosClient.put<{
        success?: boolean;
        data?: FuelDensityAdmin;
      }>(API_URLS.ADMIN.FUEL_DENSITIES.BY_ID(id), body);
      if (res.data?.success === false || !res.data?.data) {
        throw new Error(
          (res.data as { message?: string })?.message || "Update failed"
        );
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async delete(id: number): Promise<void> {
    try {
      const res = await axiosClient.delete<{
        success?: boolean;
        message?: string;
      }>(API_URLS.ADMIN.FUEL_DENSITIES.BY_ID(id));
      if (res.data?.success === false) {
        throw new Error(res.data.message || "Delete failed");
      }
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },
};
